import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Pencil, KeyRound, Trash2, Shield, ShieldCheck, UserRound, Eye, EyeOff, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { PageHeader, SectionCard, ErrorState, Stat } from "@/components/exec/Primitives";
import { useUsers } from "@/lib/queries";
import { useApp } from "@/lib/app";
import { api, errorMessage } from "@/lib/api";
import { fmtTimeAgo } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";

const PasswordField = ({ id, value, onChange, label = "Password", testId, autoComplete = "new-password" }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label} <span className="text-muted-foreground font-normal">(8+ characters)</span></Label>
      <div className="relative">
        <Input id={id} type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete} minLength={8} required className="pr-10" data-testid={testId} />
        <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"} className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground focus-gold">{show ? <EyeOff size={14} /> : <Eye size={14} />}</button>
      </div>
    </div>
  );
};

const RoleBadge = ({ role }) => (
  <Badge variant="outline" className={cn("text-[10px] font-mono uppercase tracking-wider gap-1", role === "admin" ? "border-gold/30 bg-gold/12 text-gold" : "border-border/70 bg-muted/20 text-muted-foreground")} data-testid="user-role-badge">
    {role === "admin" ? <ShieldCheck size={10} /> : <UserRound size={10} />} {role}
  </Badge>
);

export default function UsersAdmin() {
  const { user: me } = useApp();
  const qc = useQueryClient();
  const q = useUsers();
  const users = q.data?.users || [];
  const [create, setCreate] = useState(false);
  const [edit, setEdit] = useState(null);
  const [reset, setReset] = useState(null);
  const [form, setForm] = useState({ login: "", name: "", role: "user", password: "" });
  const [editForm, setEditForm] = useState({ name: "", role: "user", active: true });
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["users"] });

  useEffect(() => {
    document.title = "Zapin | Users";
  }, []);

  const openEdit = (u) => {
    setEditForm({ name: u.name || "", role: u.role, active: u.active });
    setEdit(u);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password needs at least 8 characters");
    setBusy(true);
    try {
      await api.createUser({ login: form.login.trim(), name: form.name.trim() || undefined, role: form.role, password: form.password });
      toast.success(`${form.name.trim() || form.login.trim()} can now sign in`, { description: form.role === "admin" ? "Admin: full access including users, POS upload and the live feed." : "User: every analytics page except the Live Count Feed." });
      setCreate(false);
      setForm({ login: "", name: "", role: "user", password: "" });
      refresh();
    } catch (err) {
      toast.error(errorMessage(err, "Could not create the user"));
    } finally {
      setBusy(false);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.updateUser(edit.id, { name: editForm.name.trim() || undefined, role: editForm.role, active: editForm.active });
      toast.success("User updated");
      setEdit(null);
      refresh();
    } catch (err) {
      toast.error(errorMessage(err, "Could not update the user"));
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) return toast.error("Password needs at least 8 characters");
    setBusy(true);
    try {
      await api.resetUserPassword(reset.id, newPw);
      toast.success(`Password reset for ${reset.name || reset.login}`, { description: "Share the new password securely; they can change it from their user menu." });
      setReset(null);
      setNewPw("");
      refresh();
    } catch (err) {
      toast.error(errorMessage(err, "Could not reset the password"));
    } finally {
      setBusy(false);
    }
  };

  const removeUser = async (u) => {
    try {
      await api.deleteUser(u.id);
      toast.success(`${u.name || u.login} removed`);
      refresh();
    } catch (err) {
      toast.error(errorMessage(err, "Could not remove the user"));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.updateUser(u.id, { active: !u.active });
      toast.success(u.active ? `${u.name || u.login} deactivated - any open session is signed out` : `${u.name || u.login} reactivated`);
      refresh();
    } catch (err) {
      toast.error(errorMessage(err, "Could not change the account state"));
    }
  };

  const admins = users.filter((u) => u.role === "admin" && u.active).length;

  return (
    <div className="space-y-6 md:space-y-8" data-testid="users-admin-page">
      <PageHeader
        eyebrow="Administration"
        title="Users & access"
        subtitle="There is no self sign-up. Admins see everything and manage accounts; users see every analytics page except the Live Count Feed and cannot upload POS data."
        testId="users-header"
        actions={<Button size="sm" className="h-9 gap-1.5" onClick={() => setCreate(true)} data-testid="users-create-user-dialog-open-button"><UserPlus size={14} /> New user</Button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Stat label="Accounts" value={q.data ? users.length : "—"} sub={`${users.filter((u) => u.active).length} active`} testId="users-stat-total" />
        <Stat label="Admins" value={q.data ? admins : "—"} sub="active administrators" testId="users-stat-admins" />
        <Stat label="Users" value={q.data ? users.filter((u) => u.role === "user").length : "—"} sub="view-only analytics" testId="users-stat-users" />
        <Stat label="Signed in now" value={me?.name || me?.login} sub={me?.login} testId="users-stat-me" />
      </div>

      {q.isError && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} testId="users-error" />}

      <SectionCard title="Accounts" subtitle="Deactivating an account signs it out immediately. The last active admin cannot be demoted, deactivated or removed." testId="users-table-card">
        {!q.data ? (
          <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : (
          <div className="overflow-x-auto scroll-thin -mx-1">
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border/60">
                  <TableHead className="text-[11px] uppercase tracking-[0.14em]">Person</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.14em]">Role</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.14em]">Status</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.14em] hidden md:table-cell">Last sign-in</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.14em] hidden lg:table-cell">Created</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.14em] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => {
                  const isMe = u.id === me?.id;
                  return (
                    <TableRow key={u.id} className={cn("border-border/50 hover:bg-muted/20", !u.active && "opacity-60")} data-testid={`user-row-${u.login}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <span className={cn("h-8 w-8 rounded-full flex items-center justify-center shrink-0", u.role === "admin" ? "bg-gold/15 text-gold" : "bg-muted/40 text-muted-foreground")}>{u.role === "admin" ? <Shield size={14} /> : <UserRound size={14} />}</span>
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{u.name || u.login}{isMe && <span className="text-[10px] text-muted-foreground ml-1.5">(you)</span>}</div>
                            <div className="text-[11px] text-muted-foreground font-mono truncate">{u.login}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><RoleBadge role={u.role} /></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} disabled={isMe} aria-label={u.active ? "Deactivate" : "Activate"} data-testid={`user-active-${u.login}`} className="data-[state=checked]:bg-gold" />
                          <span className="text-xs text-muted-foreground">{u.active ? "active" : "deactivated"}</span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell font-mono text-xs text-muted-foreground">{u.last_login_at ? fmtTimeAgo(u.last_login_at) : "never"}</TableCell>
                      <TableCell className="hidden lg:table-cell font-mono text-xs text-muted-foreground">{fmtTimeAgo(u.created_at)}{u.created_by ? ` · by ${u.created_by}` : ""}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-0.5">
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Edit" onClick={() => openEdit(u)} data-testid={`user-edit-${u.login}`}><Pencil size={14} /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Reset password" onClick={() => { setNewPw(""); setReset(u); }} data-testid={`users-reset-password-button-${u.login}`}><KeyRound size={14} /></Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-danger" aria-label="Remove" disabled={isMe} data-testid={`user-delete-${u.login}`}><Trash2 size={14} /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid="user-delete-dialog">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="font-display">Remove {u.name || u.login}?</AlertDialogTitle>
                                <AlertDialogDescription>The account is deleted and any open session is signed out at once. Prefer deactivating if they may return.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Keep</AlertDialogCancel>
                                <AlertDialogAction className="bg-danger text-background hover:bg-danger/90" onClick={() => removeUser(u)} data-testid="user-delete-confirm">Remove account</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>

      <div className="rounded-lg border border-border/60 bg-muted/10 p-3 text-xs text-muted-foreground flex items-start gap-2"><Info size={13} className="text-gold mt-0.5 shrink-0" /><span>Passwords are stored hashed and never shown again. Sessions last 12 hours; a deactivated or removed account is rejected on its next request.</span></div>

      {/* create */}
      <Dialog open={create} onOpenChange={setCreate}>
        <DialogContent className="sm:max-w-md bg-popover/95 backdrop-blur-xl" data-testid="users-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">New user</DialogTitle>
            <DialogDescription>Create the account and share the password securely. They can change it from their user menu.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4">
            <div className="space-y-1.5"><Label htmlFor="nu-login">Email or username</Label><Input id="nu-login" value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} autoComplete="off" required minLength={2} placeholder="name@zapin.com" data-testid="users-create-login" /></div>
            <div className="space-y-1.5"><Label htmlFor="nu-name">Display name <span className="text-muted-foreground font-normal">(optional)</span></Label><Input id="nu-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Aisyah" data-testid="users-create-name" /></div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="h-10" data-testid="users-create-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover/95 backdrop-blur-xl">
                  <SelectItem value="user" data-testid="users-create-role-user">User - all analytics, no live feed, no POS upload</SelectItem>
                  <SelectItem value="admin" data-testid="users-create-role-admin">Admin - everything, including users</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <PasswordField id="nu-pw" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testId="users-create-password" />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="users-create-submit">{busy ? "Creating…" : "Create user"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* edit */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-md bg-popover/95 backdrop-blur-xl" data-testid="users-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Edit {edit?.name || edit?.login}</DialogTitle>
            <DialogDescription>Login names cannot be changed. {edit?.id === me?.id ? "You cannot demote or deactivate your own account." : ""}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4">
            <div className="space-y-1.5"><Label htmlFor="eu-name">Display name</Label><Input id="eu-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="users-edit-name" /></div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })} disabled={edit?.id === me?.id}>
                <SelectTrigger className="h-10" data-testid="users-edit-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover/95 backdrop-blur-xl">
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/10 px-3 py-2.5">
              <div><div className="text-sm">Account active</div><div className="text-[11px] text-muted-foreground">Deactivated accounts cannot sign in.</div></div>
              <Switch checked={editForm.active} onCheckedChange={(v) => setEditForm({ ...editForm, active: v })} disabled={edit?.id === me?.id} data-testid="users-edit-active" className="data-[state=checked]:bg-gold" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="users-edit-submit">{busy ? "Saving…" : "Save changes"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* reset password */}
      <Dialog open={!!reset} onOpenChange={(o) => !o && setReset(null)}>
        <DialogContent className="sm:max-w-md bg-popover/95 backdrop-blur-xl" data-testid="users-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reset password for {reset?.name || reset?.login}</DialogTitle>
            <DialogDescription>Their current sessions stay valid until they expire; the new password applies at the next sign-in.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReset} className="space-y-4">
            <PasswordField id="rp-pw" value={newPw} onChange={setNewPw} label="New password" testId="users-reset-password" />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setReset(null)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="users-reset-submit">{busy ? "Saving…" : "Set password"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
